import { ObjectToDraw } from '../types';
import Entity from './entity';
import SpriteAnimationRenderer from '../spriteAnimationRenderer';
import GameItem from './gameItem';
import TextParticle from '../particles/textParticle';
import InteractionState from '../game/interactionState';

// A static chest that holds predefined items. The player opens it with the
// melee attack (space / the mobile attack button) while standing in range.
// While in range a "[SPACE] Open" prompt is shown above the chest.
//
// Sprites (spritesheet png + json, same format as every other animation):
//   /assets/entities/chest/closed   - static, 1 frame
//   /assets/entities/chest/opening  - the opening animation, played once
//   /assets/entities/chest/open     - static, 1 frame
class Chest implements Entity {
    NAME = 'chest';
    hash: string;
    x: number;
    y: number;
    hitbox: { w: number, h: number };
    strength: number;
    damagable: boolean;
    persistent: boolean = false;
    interactRange: number = 80;

    // 'closed' -> (interact) -> 'opening' -> (animation ends) -> 'open'
    state: string;
    items: string[];
    isDead: boolean;

    animClosed: SpriteAnimationRenderer;
    animOpening: SpriteAnimationRenderer;
    animOpen: SpriteAnimationRenderer;

    //what a chest holds when nothing else was configured (GameItem types)
    static DEFAULT_ITEMS = ["coin", "coin", "coin", "heart"];

    constructor(sx: number, sy: number, items: string[] | null = null) {
        this.hash = `chest_${sx}_${sy}`;
        this.x = sx;
        this.y = sy;
        this.hitbox = {w: 32, h: 32};
        this.strength = 1;
        this.damagable = false; //opened by interacting, not damaged by hits
        this.state = 'closed';
        this.isDead = false;
        this.items = (items && items.length > 0) ? items.slice() : Chest.DEFAULT_ITEMS.slice();

        this.animClosed = new SpriteAnimationRenderer();
        this.animClosed.load('/assets/entities/chest/closed');

        this.animOpening = new SpriteAnimationRenderer();
        this.animOpening.load('/assets/entities/chest/opening');
        this.animOpening.repeats = false;

        this.animOpen = new SpriteAnimationRenderer();
        this.animOpen.load('/assets/entities/chest/open');
    }

    init() {
        console.log('Chest initialized');
    }

    update() {
        //the opening animation finished - actually open up
        if(this.state == 'opening' && !this.animOpening.playing){
            this.open();
        }
    }

    //called by the player's melee attack when in range
    interact(){
        if(this.state == 'closed'){
            if(this.animOpening.getData()){
                this.state = 'opening';
                this.animOpening.start(0);
            }else{
                //opening animation isn't available (not loaded yet or the
                //asset is missing) - open instantly instead of getting stuck
                this.open();
            }
        }else if(this.state == 'open'){
            particles.push(new TextParticle(this.x, this.y - 40, "Empty..."));
        }
    }

    open(){
        this.state = 'open';
        //pop the items out of the chest
        for(let item of this.items){
            entities.push(new GameItem(item, this.x, this.y - 8, p5js.random(2, 5)));
        }
        this.items = [];

        //an opened chest is spent - record it so it doesn't come back full
        //(or at all) when its chunk reloads
        if(this.persistent){
            InteractionState.markConsumed(this.hash);
        }
    }

    isPlayerInRange(): boolean {
        return p5js.dist(this.x, this.y, player.x, player.y) < this.interactRange;
    }

    render(objectsToDraw: ObjectToDraw[]) {
        let rX = p5js.floor(this.x);
        let rY = p5js.floor(this.y);
        let posOnScreen = {x: rX-player.x, y: rY-player.y};

        if(posOnScreen.x < -buffer.width/2-64 || posOnScreen.x > buffer.width/2+64 || posOnScreen.y < -buffer.height/2-64 || posOnScreen.y > buffer.height/2+64){
            return;
        }

        if(showDebug){
            //hitbox + interact range
            buffer.stroke(0, 0, 255);
            buffer.strokeWeight(1);
            buffer.noFill();
            buffer.rect(posOnScreen.x-this.hitbox.w/2, posOnScreen.y-this.hitbox.h, this.hitbox.w, this.hitbox.h);
            buffer.ellipse(posOnScreen.x, posOnScreen.y, this.interactRange*2, this.interactRange*2);
        }

        //shadow
        buffer.noStroke();
        buffer.fill(0, 100);
        buffer.ellipse(posOnScreen.x, posOnScreen.y, 36, 16);

        let anim = this.animClosed;
        if(this.state == 'opening'){
            anim = this.animOpening;
        }else if(this.state == 'open'){
            anim = this.animOpen;
        }

        if(anim.getData()){
            anim.render(objectsToDraw, posOnScreen.x, posOnScreen.y, 0);
        }else{
            //placeholder while the chest sprites don't exist yet
            buffer.stroke(40, 20, 0);
            buffer.strokeWeight(1);
            buffer.fill(this.state == 'open' ? 100 : 140, 80, 30);
            buffer.rect(posOnScreen.x-14, posOnScreen.y-20, 28, 20);
            buffer.line(posOnScreen.x-14, posOnScreen.y-12, posOnScreen.x+14, posOnScreen.y-12);
        }
    }

    renderUI(): void {
        //interaction prompt when the player is close enough
        if(this.state == 'closed' && this.isPlayerInRange()){
            let px = this.x-player.x;
            let py = this.y-player.y - 40;
            let prompt = isMobile ? "[ATTACK] Open" : "[SPACE] Open";

            buffer.textSize(11);
            buffer.textAlign(buffer.CENTER, buffer.CENTER);
            let tw = buffer.textWidth(prompt);

            buffer.noStroke();
            buffer.fill(0, 150);
            buffer.rect(px-tw/2-5, py-9, tw+10, 18);

            buffer.fill(255);
            buffer.text(prompt, px, py);
        }
    }

    isToBeRemoved() {
        return this.isDead;
    }

    kill() {
        this.isDead = true;
    }

    hit(damage: number){
        //chests don't take damage
    }

    getHash(): string {
        return this.hash;
    }

    isInHitbox(px: number, py: number): boolean {
        return px > this.x - this.hitbox.w/2 && px < this.x + this.hitbox.w/2 && py > this.y - this.hitbox.h && py < this.y;
    }
}

export default Chest;
